import React from "react";
import "./stylesTableQF.css";
import { iTxt } from "@duosoftbg/nacid-components";
import { getI18n, useTranslation } from "react-i18next";
import { ReactComponent as TableDashedIcon } from "./icons/table-dashed.svg";
import { ReactComponent as LayoutGrid } from "./icons/layout-grid.svg";
import { ReactComponent as ButtonUp } from "./icons/ButtonUp.svg";
import { ReactComponent as ButtonLeft } from "./icons/ButtonLeft.svg";
import { ReactComponent as ButtonRight } from "./icons/ButtonRight.svg";
import { ReactComponent as ButtonDown } from "./icons/ButtonDown.svg";
import Tooltip from "@mui/material/Tooltip";
import { getColor, tableTooltipProps } from "./stylesTableQfTS";
import {
  mainHeaders,
  sideHeaders,
  subHeaders1,
  subHeaders2,
  tooltipMainHeader,
  tooltipSideHeader,
} from "./dataTableQf";
import { CountryDetailsLong } from "../../../types/QFDataTypes";

interface CountryQFTableProps {
  countryData: CountryDetailsLong;
  handleTables: (direction: "H" | "V") => void;
  setLogicalTableMapH: (value: boolean[][]) => void;
  setColumnSwitchH: (value: boolean[]) => void;
  setRowSwitchH: (value: boolean[]) => void;
  logicalTableMapH: boolean[][];
  columnSwitchH: boolean[];
  rowSwitchH: boolean[];
}

export const QFTableHorizontal = ({
  countryData,
  handleTables,
  setLogicalTableMapH,
  setColumnSwitchH,
  setRowSwitchH,
  logicalTableMapH,
  columnSwitchH,
  rowSwitchH,
}: CountryQFTableProps) => {
  const { t } = useTranslation();

  const handleColumnSwitchH = (index: number) => {
    // @ts-ignore
    setLogicalTableMapH((prev: boolean[][]) => {
      return prev.map((row) => row.map((cell, c) => (c === index ? !columnSwitchH[index] : cell)));
    });
    // @ts-ignore
    setColumnSwitchH((prev: boolean[]) => {
      return prev.map((val, i) => (i === index ? !val : val));
    });
  };

  const handleRowSwitchH = (index: number) => {
    // @ts-ignore
    setLogicalTableMapH((prev: boolean[][]) => {
      return prev.map((row, i) => {
        return i === index ? row.map(() => !rowSwitchH[index]) : row;
      });
    });

    // @ts-ignore
    setRowSwitchH((prev: boolean[]) => {
      return prev.map((val, i) => (i === index ? !val : val));
    });
  };

  const isWholeTableHiddenH = () => {
    return rowSwitchH.every((l) => l === true) && columnSwitchH.every((l) => l === true);
  };

  const handleWholeTableH = () => {
    // @ts-ignore
    setLogicalTableMapH((prev: boolean[][]) => {
      return prev.map((row) => row.map(() => !isWholeTableHiddenH()));
    });
  };

  const isHiddenCell = (rowIndex: number, colIndex: number) => {
    return logicalTableMapH[rowIndex][colIndex] === true;
  };
  const isMainHeaderCellHide = (index: number) => {
    return logicalTableMapH.map((col) => col[index]).every((l) => l === true);
  };
  const isSideHeaderCellHide = (index: number) => {
    return logicalTableMapH.map((col) => col[index + 2]).every((l) => l === true);
  };
  const cornerButtonsFlag =
    logicalTableMapH.map((col) => col[0]).every((l) => l === true) &&
    logicalTableMapH.map((col) => col[1]).every((l) => l === true);

  const spanRowHeaders_0 = isMainHeaderCellHide(0) && !isMainHeaderCellHide(1) ? 3 : 2;
  const spanRowHeaders_1 = !isMainHeaderCellHide(0) && isMainHeaderCellHide(1) ? 3 : 2;
  const spanRowSideHeaders = cornerButtonsFlag ? 2 : 3;

  const tableMap = new Map<number, number>();
  for (let i = 0; i < 3; i++) {
    tableMap.set(
      countryData.countriesQF.find((qf) => qf.eqfLevel.id === 8 - i)?.level,
      countryData.countriesQF
        .filter((qf) => qf.eqfLevel.id === 8 - i)
        .reduce((sum: number, el) => sum + (!logicalTableMapH[i][1] ? el.levels.length : 1), 0),
    );
  }

  return (
    <div
      key="table"
      className="table fade-slide"
      style={{ gridTemplateColumns: cornerButtonsFlag ? `40px 40px repeat(11, auto)` : `40px  repeat(12, auto)` }}
    >
      {/*  1 or 2 tables-control button(s) */}
      <div
        key="btnD"
        className="iconPush-cell"
        style={{
          gridColumn: cornerButtonsFlag ? `span 1` : `span 2`,
        }}
      >
        <TableDashedIcon onClick={() => handleTables("H")} />
      </div>
      {cornerButtonsFlag && (
        <div key="btnL" className="iconPop-cell" style={{ gridColumn: `span 1` }}>
          <LayoutGrid onClick={() => handleTables("V")} />
        </div>
      )}

      {/* 2 main headers*/}
      {mainHeaders(t, countryData, isMainHeaderCellHide).map((title, index) => (
        <Tooltip
          key={isMainHeaderCellHide(index) ? `ttm-c-${index}` : `ttm-o-${index}`}
          title={tooltipMainHeader(t, countryData)[index]}
          placement="bottom"
          componentsProps={tableTooltipProps}
          disableHoverListener={!isMainHeaderCellHide(index)}
        >
          <div
            key={`mh-${index}`}
            className="header-cell fade-slide"
            style={{
              gridRow: `span ${index === 0 ? spanRowHeaders_0 : spanRowHeaders_1}`,
              gridColumn: `span 4`,
              backgroundColor: getColor(1),
            }}
          >
            <span style={{ paddingRight: "25px" }}>{title}</span>
            {!columnSwitchH[index] ? (
              <ButtonLeft className="iconButtonLeftRight-cell" onClick={() => handleColumnSwitchH(index)} />
            ) : (
              <ButtonRight className="iconButtonLeftRight-cell" onClick={() => handleColumnSwitchH(index)} />
            )}
          </div>
        </Tooltip>
      ))}

      {/* 3 side headers */}
      {sideHeaders(t, isSideHeaderCellHide).map((title, index) => (
        <Tooltip
          key={isSideHeaderCellHide(index) ? `tts-c-${index}` : `tts-o-${index}`}
          title={tooltipSideHeader(t)[index]}
          placement="bottom"
          componentsProps={tableTooltipProps}
          disableHoverListener={!isSideHeaderCellHide(index)}
        >
          <div
            key={`sh-${index}`}
            className="header-cell fade-slide"
            style={{
              gridRow: `span ${spanRowSideHeaders}`,
              backgroundColor: getColor(1),
            }}
          >
            <span style={{ paddingRight: "25px", marginBottom: "1px" }}>{title}</span>
            {!columnSwitchH[index + 2] ? (
              <ButtonLeft className="iconButtonLeftRight-cell" onClick={() => handleColumnSwitchH(index + 2)} />
            ) : (
              <ButtonRight className="iconButtonLeftRight-cell" onClick={() => handleColumnSwitchH(index + 2)} />
            )}
          </div>
        </Tooltip>
      ))}
      {/* 2nd and 3rd botton to control whole table*/}
      {!cornerButtonsFlag && (
        <div key="btnL" className="iconPop-cell" style={{ gridColumn: `span 2` }}>
          <LayoutGrid onClick={() => handleTables("V")} />
        </div>
      )}
      <div key="btnU" className="iconButtonUp-cell" style={{ backgroundColor: getColor(2), gridColumn: `span 2` }}>
        {!isWholeTableHiddenH() ? (
          <ButtonUp onClick={() => handleWholeTableH()} />
        ) : (
          <ButtonDown onClick={() => handleWholeTableH()} />
        )}
      </div>
      {/* 1st array of subheaders*/}
      {!isMainHeaderCellHide(0) &&
        subHeaders1(t).map((title, index) => (
          <div
            key={`subH-0-${index}`}
            className="subheader-cell fade-slide"
            style={{ gridRow: `span 1`, backgroundColor: getColor(2) }}
          >
            {title}
          </div>
        ))}
      {/* 2nd array of subheaders */}
      {!isMainHeaderCellHide(1) &&
        subHeaders2(t).map((title, index) => (
          <div
            key={`subH-1-${index}`}
            className="subheader-cell fade-slide"
            style={{ gridRow: `span 1`, backgroundColor: getColor(2) }}
          >
            {title}
          </div>
        ))}

      {countryData.countriesQF.map((countryQF, qfIndex) => {
        const levels = countryQF.levels || [];
        const firstLevel = levels[0];
        const restLevels = levels.slice(1);

        const subLevelMessageBg = `${levels.length} ${levels.length === 1 ? "степен" : "степени"}`;
        const subLevelMessageEn = `${levels.length} ${levels.length === 1 ? "degree" : "degrees"}`;
        return (
          <React.Fragment key={`qf-block-${qfIndex}`}>
            {/* 1st row-control button and f1rst data cells */}
            <div
              key={`btnU-${qfIndex}`}
              className="iconButtonUp-cell"
              style={{
                backgroundColor: getColor(countryQF.eqfLevel.id),
                gridRow: `span ${isHiddenCell(qfIndex, 1) ? 1 : levels.length}`,
                gridColumn: `span 2`,
              }}
            >
              {!rowSwitchH[qfIndex] ? (
                <ButtonUp onClick={() => handleRowSwitchH(qfIndex)} />
              ) : (
                <ButtonDown onClick={() => handleRowSwitchH(qfIndex)} />
              )}
            </div>
            {/* 1 - 4 columns */}
            <div
              key={`cellA-${qfIndex}-${isHiddenCell(qfIndex, 0) ? 1 : 0}`}
              className="common-cell fade-slide"
              style={{
                backgroundColor: getColor(countryQF.eqfLevel.id),
                gridColumn: `span ${isHiddenCell(qfIndex, 0) ? 4 : 1}`,
                gridRow: `span ${isHiddenCell(qfIndex, 1) ? 1 : levels.length}`,
              }}
            >
              <div style={{ fontWeight: "600" }}>{iTxt(getI18n().language, countryQF.name, countryQF.nameEn)}</div>
              {!isHiddenCell(qfIndex, 0) && (
                <div>{iTxt(getI18n().language, countryQF.description, countryQF.descriptionEn)}</div>
              )}
            </div>
            {!isHiddenCell(qfIndex, 0) && (
              <>
                <div
                  key={`cellB-${qfIndex}`}
                  className="common-cell fade-slide"
                  style={{
                    backgroundColor: getColor(countryQF.eqfLevel.id),
                    gridRow: `span ${isHiddenCell(qfIndex, 1) ? 1 : levels.length}`,
                  }}
                >
                  {countryQF.nameNative}
                </div>

                <div
                  key={`cellC-${qfIndex}`}
                  className="common-cell fade-slide"
                  style={{
                    backgroundColor: getColor(countryQF.eqfLevel.id),
                    gridRow: `span ${isHiddenCell(qfIndex, 1) ? 1 : levels.length}`,
                  }}
                >
                  {countryQF.credits ? `${countryQF.credits} ${countryData.creditsLabel}` : "-"}
                </div>

                <div
                  key={`cellD-${qfIndex}`}
                  className="common-cell fade-slide"
                  style={{
                    backgroundColor: getColor(countryQF.eqfLevel.id),
                    gridRow: `span ${isHiddenCell(qfIndex, 1) ? 1 : levels.length}`,
                  }}
                >
                  {countryQF.duration ? `${countryQF.duration}${iTxt(getI18n().language, " г.", " y")}` : "-"}
                </div>
              </>
            )}

            {/* First row of 2-4 columns - 2nd data cells - first row only */}
            <div
              key={`cellE-${qfIndex}-${isHiddenCell(qfIndex, 1) ? 1 : 0}`}
              className="common-cell fade-slide"
              style={{
                gridRow: `span ${isHiddenCell(qfIndex, 1) ? 1 : 1}`,
                gridColumn: `span ${isHiddenCell(qfIndex, 1) ? 4 : 1}`,
                backgroundColor: getColor(countryQF.eqfLevel.id),
              }}
            >
              {isHiddenCell(qfIndex, 1)
                ? iTxt(getI18n().language, subLevelMessageBg, subLevelMessageEn)
                : iTxt(getI18n().language, firstLevel.name, firstLevel.nameEn)}
            </div>
            {!isHiddenCell(qfIndex, 1) && (
              <>
                <div
                  key={`cellF-${qfIndex}`}
                  className="common-cell fade-slide"
                  style={{
                    backgroundColor: getColor(countryQF.eqfLevel.id),
                  }}
                >
                  {firstLevel.nameNative}
                </div>

                <div
                  key={`cellG-${qfIndex}`}
                  className="common-cell fade-slide"
                  style={{
                    backgroundColor: getColor(countryQF.eqfLevel.id),
                  }}
                >
                  {firstLevel.credits ? `${firstLevel.credits} ${countryData.creditsLabel}` : "-"}
                </div>

                <div
                  key={`cellH-${qfIndex}`}
                  className="common-cell fade-slide"
                  style={{
                    backgroundColor: getColor(countryQF.eqfLevel.id),
                  }}
                >
                  {firstLevel.duration ? `${firstLevel.duration}${iTxt(getI18n().language, " г.", " y")}` : "-"}
                </div>
              </>
            )}

            {/* Last 3: EQF, Bologna Cycle, BG Level */}
            {tableMap.has(countryQF.level) && (
              <React.Fragment key={`extra-block-${qfIndex}`}>
                <div
                  key={`cellI-${qfIndex}-${isHiddenCell(qfIndex, 2) ? 1 : 0}`}
                  className="common-cell fade-slide"
                  style={{
                    backgroundColor: getColor(countryQF.eqfLevel.id),
                    gridRow: `span  ${tableMap.get(countryQF.level)}`,
                  }}
                >
                  <div style={{ fontWeight: `bold` }}>
                    {iTxt(getI18n().language, countryQF?.eqfLevel?.name, countryQF.eqfLevel?.nameEn)}
                  </div>
                  {!isHiddenCell(qfIndex, 2) && (
                    <div>
                      {iTxt(getI18n().language, countryQF?.eqfLevel?.description, countryQF?.eqfLevel?.descriptionEn)}
                    </div>
                  )}
                </div>

                <div
                  key={`cellJ-${qfIndex}-${isHiddenCell(qfIndex, 3) ? 1 : 0}`}
                  className="common-cell fade-slide"
                  style={{
                    backgroundColor: getColor(countryQF.eqfLevel.id),
                    gridRow: `span  ${tableMap.get(countryQF.level)}`,
                  }}
                >
                  <div style={{ fontWeight: `bold` }}>
                    {iTxt(getI18n().language, countryQF?.bolognaCycle?.name, countryQF?.bolognaCycle?.nameEn)}
                  </div>
                  {!isHiddenCell(qfIndex, 3) && (
                    <div>
                      {iTxt(
                        getI18n().language,
                        countryQF?.bolognaCycle?.description,
                        countryQF?.bolognaCycle?.descriptionEn,
                      )}
                    </div>
                  )}
                </div>

                <div
                  key={`cellK-${qfIndex}-${isHiddenCell(qfIndex, 4) ? 1 : 0}`}
                  className="common-cell fade-slide"
                  style={{
                    backgroundColor: getColor(countryQF.eqfLevel.id),
                    gridRow: `span  ${tableMap.get(countryQF.level)}`,
                  }}
                >
                  <div style={{ fontWeight: `bold` }}>
                    {iTxt(getI18n().language, countryQF?.bgLevel?.name, countryQF?.bgLevel?.nameEn)}
                  </div>
                  {!isHiddenCell(qfIndex, 4) && (
                    <div>
                      {iTxt(getI18n().language, countryQF?.bgLevel?.description, countryQF?.bgLevel?.descriptionEn)}
                    </div>
                  )}
                </div>
              </React.Fragment>
            )}
            {/*rest rows of the subLevels data cells*/}
            {!isHiddenCell(qfIndex, 1) &&
              restLevels.map((subLevel, idx) => (
                <React.Fragment key={`rest-${qfIndex}-${idx}`}>
                  <div
                    key={`cellA-${qfIndex}-${idx}`}
                    className="common-cell fade-slide"
                    style={{
                      backgroundColor: getColor(countryQF.eqfLevel.id),
                    }}
                  >
                    {iTxt(getI18n().language, subLevel.name, subLevel.nameEn)}
                  </div>
                  <div
                    key={`cellB-${qfIndex}-${idx}`}
                    className="common-cell fade-slide"
                    style={{
                      backgroundColor: getColor(countryQF.eqfLevel.id),
                    }}
                  >
                    {subLevel.nameNative}
                  </div>
                  <div
                    key={`cellC-${qfIndex}-${idx}`}
                    className="common-cell fade-slide"
                    style={{
                      backgroundColor: getColor(countryQF.eqfLevel.id),
                    }}
                  >
                    {subLevel.credits ? `${subLevel.credits} ${countryData.creditsLabel}` : "-"}
                  </div>
                  <div
                    key={`cellD-${qfIndex}-${idx}`}
                    className="common-cell fade-slide"
                    style={{
                      backgroundColor: getColor(countryQF.eqfLevel.id),
                    }}
                  >
                    {subLevel.duration ? `${subLevel.duration}${iTxt(getI18n().language, " г.", " y")}` : "-"}
                  </div>
                </React.Fragment>
              ))}
          </React.Fragment>
        );
      })}
    </div>
  );
};
